import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import useDeviceKeys from './hooks/useDeviceKeys';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';

interface CreateDeviceKeyFormValues {
    name: string;
    keyId: string;
    secretAccessKey: string;
}

export default function CreateDeviceKey() {
    const { orgId } = useUserOrg();
    const navigate = useNavigate();
    const [isGeneratingKey, setIsGeneratingKey] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [keyPair, setKeyPair] = useState<{
        keyId: string;
        secretAccessKey: string;
    } | null>(null);
    const { generateDeviceKey, addDeviceKey } = useDeviceKeys(orgId);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            const keyPair = await generateDeviceKey();

            if (isMounted) {
                setKeyPair(keyPair);
                setIsGeneratingKey(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isGeneratingKey) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const initialValues: CreateDeviceKeyFormValues = {
        name: '',
        keyId: keyPair?.keyId || '',
        secretAccessKey: keyPair?.secretAccessKey || '',
    };

    return (
        <div className="p-6">
            <Card>
                <CardHeader>
                    <CardTitle>Create Device Key</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Device keys are downloaded automatically upon creation.
                        The secret key cannot be retrieved after leaving this
                        page.
                    </p>
                </CardHeader>
                <CardContent>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={async (values, actions) => {
                            const wasSuccessful = await addDeviceKey(values);
                            actions.setSubmitting(false);

                            if (wasSuccessful) {
                                const element = document.createElement('a');
                                const configFile = new Blob(
                                    [JSON.stringify(values)],
                                    {
                                        type: 'text/plain',
                                    },
                                );
                                element.href = URL.createObjectURL(configFile);
                                element.download = `${values.name}_device_key.json`;
                                document.body.appendChild(element);
                                element.click();

                                navigate('/device-management#keys');
                            }
                        }}
                    >
                        <Form
                            className="space-y-6"
                            placeholder={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                        >
                            <div className="space-y-2">
                                <Label htmlFor="name">Device Name</Label>
                                <Field
                                    as={Input}
                                    id="name"
                                    name="name"
                                    placeholder=""
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="keyId">Key ID</Label>
                                <Field
                                    as={Input}
                                    id="keyId"
                                    name="keyId"
                                    placeholder=""
                                    readOnly
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="secretAccessKey">
                                    Secret Access Key
                                </Label>
                                <div className="flex items-center gap-2">
                                    <Field
                                        as={Input}
                                        id="secretAccessKey"
                                        name="secretAccessKey"
                                        placeholder=""
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        readOnly
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                    >
                                        {showPassword ? (
                                            <EyeOff className="size-4" />
                                        ) : (
                                            <Eye className="size-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button type="submit">
                                    Create Device Key
                                </Button>
                                <NavLink to="/device-management#keys">
                                    <Button type="button" variant="outline">
                                        Cancel
                                    </Button>
                                </NavLink>
                            </div>
                        </Form>
                    </Formik>
                </CardContent>
            </Card>
        </div>
    );
}
